import { DatePicker, Modal, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { AccountLoginCode } from '@/stores';
import { authStore, brandStore, loginCodeStore } from '@/stores';

interface UpdateExpiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loginCode: AccountLoginCode | null;
}

export function UpdateExpiryDialog({
  open,
  onOpenChange,
  loginCode,
}: UpdateExpiryDialogProps) {
  const [expiresAt, setExpiresAt] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!open) {
      setExpiresAt(null);
      return;
    }
    setExpiresAt(loginCode?.expiresAt ? dayjs(loginCode.expiresAt) : null);
  }, [loginCode, open]);

  const handleSubmit = async () => {
    if (!brandStore.activeBrandAppId || !loginCode || !expiresAt) {
      return;
    }

    try {
      await loginCodeStore.updateLoginCodeExpiry(brandStore.activeBrandAppId, {
        id: loginCode.id,
        expires_at: expiresAt.toISOString(),
        updated_by: authStore.user?.user_id,
      });
      message.success('过期时间已更新');
      onOpenChange(false);
    } catch (error) {
      console.error('更新过期时间失败:', error);
      message.error('更新过期时间失败，请稍后重试');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange(false)}
      title="设置过期时间"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!expiresAt || loginCodeStore.updating}
          >
            {loginCodeStore.updating ? '保存中...' : '保存'}
          </Button>
        </div>
      }
      centered
      width={520}
    >
      <div className="space-y-4 pt-2">
        <div className="rounded-lg border border-border bg-slate-50 p-3">
          <p className="text-xs text-muted-foreground">当前登录码</p>
          <p className="mt-1 font-mono text-sm text-foreground">
            {loginCode?.codeMasked || '-'}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            用户：{loginCode?.displayName || '-'}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="updateExpiry">新的过期时间 *</Label>
          <DatePicker
            id="updateExpiry"
            showTime
            value={expiresAt}
            onChange={(value) => setExpiresAt(value)}
            className="w-full"
            disabledDate={(current) =>
              Boolean(current && current < dayjs().startOf('day'))
            }
          />
        </div>
      </div>
    </Modal>
  );
}
